// frontend/src/components/assessment/hooks/useAssessmentSession.js
//
// Manages the assessment session state object.
// This is the single source of truth for both the CaseStudyForm and ScriptEditor.
//
// Source rules:
//   'none'  — field is empty and untouched
//   'ai'    — field was populated by the AI (left border accent in form)
//   'user'  — field was written or edited by the user; permanent, AI cannot overwrite

import { useState, useCallback } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Initial session shape — all fields empty
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_FIELD = { content: null, source: 'none' };

const SCRIPT_PHASES = new Set(['narration', 'inner_monologue']);
const NO_ARTIFACT_PHASES = new Set(['situation', 'goals']);
const REPORT_PHASES = new Set(['reporting']);

const phaseToFocusArtifact = (phase) => {
  if (NO_ARTIFACT_PHASES.has(phase)) return null;
  if (SCRIPT_PHASES.has(phase)) return 'script';
  if (REPORT_PHASES.has(phase)) return 'report';
  return 'caseStudy';
};

const ARTIFACT_UNLOCK_ORDER = ['caseStudy', 'script', 'report'];

function seenTabsFromPhase(phase) {
  const artifact = phaseToFocusArtifact(phase);
  if (!artifact) {
    // goals comes after script is unlocked; situation has nothing yet
    return phase === 'goals' ? ['caseStudy', 'script'] : [];
  }
  const idx = ARTIFACT_UNLOCK_ORDER.indexOf(artifact);
  return ARTIFACT_UNLOCK_ORDER.slice(0, idx + 1);
}

function buildEmptySession() {
  const f = () => ({ ...EMPTY_FIELD });
  return {
    currentPhase: 'situation',
    currentSubphase: 'active',
    caseStudy: {
      characterBrief: {
        inSum:              f(),
        name:               f(),
        role:               f(),
        organization:       f(),
        personality:        f(),
        communicationStyle: f(),
        background:         f(),
      },
      realIssue: {
        summary:          f(),
        dataAndReasoning: f(),
      },
      characterStakes: {
        downSides: { inSum: f() },
        upSides:   { inSum: f() },
      },
      desiredOutcome: f(),
      challenge:      f(),
    },
    script: [],
    userGoals: {
      learningGoal: f(),
    },
    report: null,
    focusArtifact: null,
    seenTabs: [],
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Apply AI case study updates with source protection
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Merge an AI caseStudyUpdates partial into the current caseStudy state.
 * updates uses flat canonical names (characterBriefInSum, realIssueSummary, …).
 * Fields with source 'user' are never overwritten.
 */
/**
 * True when `incoming` keeps every character of `existing` (whitespace-normalised) and
 * is strictly longer — the user added to their text without any of it being removed.
 * Mirrors the backend check in justification/responseProcessor.js.
 */
function isNonDestructiveAppend(existing, incoming) {
  if (existing == null || incoming == null) return false;
  const norm = (s) => String(s).replace(/\s+/g, ' ').trim();
  const cur = norm(existing);
  const nv  = norm(incoming);
  return cur.length > 0 && nv.length > cur.length && nv.includes(cur);
}

function applyAICaseStudyUpdates(currentCaseStudy, updates) {
  if (!updates) return currentCaseStudy;

  const applyField = (current, newValue) => {
    if (newValue == null) return current;
    // Always apply what the server sent. The backend (justification guard) is the single
    // gatekeeper for user-owned fields — it only emits a non-destructive augmentation (active)
    // or a user-directed edit (confirmation), so anything that reaches us is already sanctioned.
    // Re-adjudicating here previously DROPPED valid appends whenever our local copy didn't
    // byte-match the text the AI reproduced — that is what made augmented fields lag a turn or
    // need a refresh. Source is best-effort: an addition that preserves the user's text stays
    // 'user' (so it remains protected); anything else is 'ai'. A wrong guess here is only a
    // styling flag and self-heals on the next hydrate — it never blocks the content update.
    const keepsUserText =
      current?.source === 'user' && current?.content && isNonDestructiveAppend(current.content, newValue);
    return { content: newValue, source: keepsUserText ? 'user' : 'ai' };
  };

  const next = {
    ...currentCaseStudy,
    characterBrief:  { ...currentCaseStudy.characterBrief },
    realIssue:       { ...currentCaseStudy.realIssue },
    characterStakes: {
      downSides: { ...(currentCaseStudy.characterStakes?.downSides) },
      upSides:   { ...(currentCaseStudy.characterStakes?.upSides) },
    },
  };

  if (updates.characterBriefInSum != null)
    next.characterBrief.inSum = applyField(next.characterBrief.inSum, updates.characterBriefInSum);
  if (updates.realIssueSummary != null)
    next.realIssue.summary = applyField(next.realIssue.summary, updates.realIssueSummary);
  if (updates.realIssueDataAndReasoning != null)
    next.realIssue.dataAndReasoning = applyField(next.realIssue.dataAndReasoning, updates.realIssueDataAndReasoning);
  if (updates.characterStakesDownSidesInSum != null)
    next.characterStakes.downSides.inSum = applyField(next.characterStakes.downSides?.inSum, updates.characterStakesDownSidesInSum);
  if (updates.characterStakesUpSidesInSum != null)
    next.characterStakes.upSides.inSum = applyField(next.characterStakes.upSides?.inSum, updates.characterStakesUpSidesInSum);
  if (updates.challenge != null)
    next.challenge = applyField(next.challenge, updates.challenge);
  if (updates.desiredOutcome != null)
    next.desiredOutcome = applyField(next.desiredOutcome, updates.desiredOutcome);

  return next;
}

// ─────────────────────────────────────────────────────────────────────────────
// Apply a single case study field update (user edit)
// Flat name → nested state path mapping
// ─────────────────────────────────────────────────────────────────────────────

function applyCaseStudyUserEdit(currentCaseStudy, field, value) {
  const leaf = { content: value, source: 'user' };

  switch (field) {
    case 'characterBriefInSum':
      return { ...currentCaseStudy, characterBrief: { ...currentCaseStudy.characterBrief, inSum: leaf } };
    case 'realIssueSummary':
      return { ...currentCaseStudy, realIssue: { ...currentCaseStudy.realIssue, summary: leaf } };
    case 'realIssueDataAndReasoning':
      return { ...currentCaseStudy, realIssue: { ...currentCaseStudy.realIssue, dataAndReasoning: leaf } };
    case 'characterStakesDownSidesInSum':
      return {
        ...currentCaseStudy,
        characterStakes: {
          ...currentCaseStudy.characterStakes,
          downSides: { ...currentCaseStudy.characterStakes?.downSides, inSum: leaf },
        },
      };
    case 'characterStakesUpSidesInSum':
      return {
        ...currentCaseStudy,
        characterStakes: {
          ...currentCaseStudy.characterStakes,
          upSides: { ...currentCaseStudy.characterStakes?.upSides, inSum: leaf },
        },
      };
    case 'desiredOutcome':
      return { ...currentCaseStudy, desiredOutcome: leaf };
    case 'challenge':
      return { ...currentCaseStudy, challenge: leaf };
    default:
      return currentCaseStudy;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Apply AI goals updates with source protection
// ─────────────────────────────────────────────────────────────────────────────

function applyAIGoalsUpdates(currentUserGoals, updates) {
  if (!updates) return currentUserGoals;
  const next = { ...currentUserGoals };
  const applyField = (current, newValue) => {
    if (current.source === 'user' && current.content) return current;
    if (!newValue) return current;
    return newValue;
  };
  if (updates.learningGoal) next.learningGoal = applyField(next.learningGoal, updates.learningGoal);
  return next;
}

// ─────────────────────────────────────────────────────────────────────────────
// Apply AI script updates
// ─────────────────────────────────────────────────────────────────────────────

function applyAIScriptUpdates(currentScript, updates) {
  if (!updates) return currentScript;

  let next = [...currentScript];
  for (const u of (updates.slotUpdates || [])) {
    const currentSlot = next[u.pairIndex]?.[u.slot];
    if (u.pairIndex < next.length && !(currentSlot?.source === 'user' && currentSlot?.content)) {
      next[u.pairIndex] = {
        ...next[u.pairIndex],
        [u.slot]: { content: u.value.content, source: 'ai' },
      };
    }
  }

  // Corrections overwrite the addressed slot REGARDLESS of source — including 'user'. This is
  // a deliberate exception to the user-permanence rule: the AI only emits a correction when the
  // user explicitly asked it to fix that line, so it is user-consented by construction. pairIndex
  // is 0-based and positional (same convention as slotUpdates).
  for (const c of (updates.corrections || [])) {
    if (c.pairIndex >= 0 && c.pairIndex < next.length) {
      next[c.pairIndex] = {
        ...next[c.pairIndex],
        [c.slot]: { content: c.value.content, source: 'ai' },
      };
    }
  }

  if (updates.pairs?.length) {
    const newPairs = updates.pairs.map((p, i) => ({
      pairId: `pair_${next.length + i + 1}`,
      me:   p.me   ? { content: p.me.content,   source: 'ai' } : { content: null, source: 'none' },
      them: p.them ? { content: p.them.content, source: 'ai' } : { content: null, source: 'none' },
    }));

    if (updates.action === 'append' || updates.afterPairIndex == null) {
      next = [...next, ...newPairs];
    } else {
      const idx = updates.afterPairIndex + 1;
      next.splice(idx, 0, ...newPairs);
    }
  }

  return next;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hydrate session from backend GET /:id/session response
// ─────────────────────────────────────────────────────────────────────────────

const normalisePhase = (phase) => phase === 'justification' ? 'case_study' : phase;

function normaliseScriptSlot(raw, fallbackSource = 'ai') {
  if (!raw) return { content: null, source: 'none' };
  if (typeof raw === 'string') return { content: raw, source: fallbackSource };
  return raw;
}

function hydrateFromBackend(data) {
  const empty = buildEmptySession();
  const phase = normalisePhase(data.currentPhase || 'situation');
  return {
    currentPhase:    phase,
    currentSubphase: data.currentSubphase || 'active',
    caseStudy:       data.caseStudy       || empty.caseStudy,
    script:        (data.script || []).map((pair, i) => ({
      pairId:       pair.pairId || `pair_${i + 1}`,
      me:           normaliseScriptSlot(pair.me),
      them:         normaliseScriptSlot(pair.them),
      me_thought:   normaliseScriptSlot(pair.me_thought),
      them_thought: normaliseScriptSlot(pair.them_thought),
    })),
    userGoals:     data.userGoals     || empty.userGoals,
    report:        data.report        || null,
    focusArtifact: phaseToFocusArtifact(phase),
    seenTabs: seenTabsFromPhase(phase),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useAssessmentSession() {
  const [session, setSession] = useState(buildEmptySession);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);

  const hydrateSession = useCallback((data) => {
    setSession(hydrateFromBackend(data));
    setSessionLoading(false);
  }, []);

  const setSessionLoadError = useCallback((err) => {
    setSessionError(err);
    setSessionLoading(false);
  }, []);

  const applyAIResponse = useCallback(({ caseStudyUpdates, scriptUpdates, userGoalsUpdates, currentPhase, currentSubphase, reportData }) => {
    setSession(prev => {
      const nextCaseStudy = applyAICaseStudyUpdates(prev.caseStudy, caseStudyUpdates);
      const nextScript    = applyAIScriptUpdates(prev.script, scriptUpdates);
      const nextUserGoals = applyAIGoalsUpdates(prev.userGoals, userGoalsUpdates);
      const nextPhase     = normalisePhase(currentPhase || prev.currentPhase);

      const canonicalArtifact = phaseToFocusArtifact(nextPhase);
      const phaseChanged      = nextPhase !== prev.currentPhase;
      const reportJustArrived = reportData != null && prev.report == null;
      const nextFocusArtifact = (phaseChanged || reportJustArrived)
        ? (canonicalArtifact ?? prev.focusArtifact)
        : prev.focusArtifact;

      const nextSeenTabs = (canonicalArtifact && !prev.seenTabs.includes(canonicalArtifact))
        ? [...prev.seenTabs, canonicalArtifact]
        : prev.seenTabs;

      return {
        ...prev,
        currentPhase:    nextPhase,
        currentSubphase: currentSubphase || prev.currentSubphase,
        caseStudy:       nextCaseStudy,
        script:          nextScript,
        userGoals:       nextUserGoals,
        report:          reportData != null ? reportData : prev.report,
        focusArtifact:   nextFocusArtifact,
        seenTabs:        nextSeenTabs,
      };
    });
  }, []);

  /**
   * Update a case study field directly (user edit).
   * field is a flat canonical name: characterBriefInSum, realIssueSummary, etc.
   * source flips to 'user' immediately and is permanent.
   */
  const updateCaseStudyField = useCallback((field, value) => {
    setSession(prev => ({
      ...prev,
      caseStudy: applyCaseStudyUserEdit(prev.caseStudy, field, value),
    }));
  }, []);

  const updateScriptTurn = useCallback((pairId, side, value) => {
    setSession(prev => ({
      ...prev,
      script: prev.script.map(pair =>
        pair.pairId === pairId
          ? { ...pair, [side]: { content: value, source: 'user' } }
          : pair
      ),
    }));
  }, []);

  const replaceScript = useCallback((newScript) => {
    setSession(prev => ({ ...prev, script: newScript }));
  }, []);

  const updateGoalsField = useCallback((field, value) => {
    setSession(prev => ({
      ...prev,
      userGoals: {
        ...prev.userGoals,
        [field]: { content: value, source: 'user' },
      },
    }));
  }, []);

  const setFocusArtifact = useCallback((tab) => {
    setSession(prev => ({
      ...prev,
      focusArtifact: tab,
      seenTabs: prev.seenTabs.includes(tab) ? prev.seenTabs : [...prev.seenTabs, tab],
    }));
  }, []);

  return {
    session,
    sessionLoading,
    sessionError,
    hydrateSession,
    setSessionLoadError,
    applyAIResponse,
    updateCaseStudyField,
    updateScriptTurn,
    replaceScript,
    updateGoalsField,
    setFocusArtifact,
  };
}